import React, { useContext, FormEvent } from 'react';
import { POSTED_DATE_OPTIONS } from '../../constants/news_search.constants';
/* eslint-disable-next-line max-len */
import TypeaheadSelectBootstrap5 from '../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import { autocompleteAuthorsPath } from '../../../utilities/api_relative_paths';
/* eslint-disable-next-line max-len */
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import { Context } from '../../context/news_search.context';
import Collapse from 'react-bootstrap/Collapse';
import SavedSearches from '../saved_searches/saved_searches';
import styles from './header.module.scss';
import headerStyles from '../../../ui_toolbox/sass_modules/search_header.module.scss';
import SearchCreation from '../search_creation/search_creation';

const Container: React.FC = () => {
  const {
    isLoading,
    queryInput,
    setQueryInput,
    query,
    setQuery,
    setPageNumber,
    setPostedDate,
    postedDate,
    authors,
    setAuthors,
    topics,
    setTopics,
    topicOptions,
    isSignedIn,
    displaySavedSearches,
    setDisplaySavedSearches,
    clearAllFilters,
    savedSearchId,
    sponsored,
  } = useContext(Context);

  const handleQueryChange = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setPageNumber(1);
    setQuery(queryInput.trim().length ? queryInput : undefined);
  };

  const renderKeywordInput: JSX.Element = (
    <form onSubmit={handleQueryChange}>
      <div className='form-group main-filters'>
        <div className='input-group'>
          <input
            className={`form-control ${styles.HeaderBoxBorder}`}
            aria-label='keyword filter'
            autoFocus={true}
            type='text'
            onChange={({ target }) => setQueryInput(target.value)}
            placeholder='Enter keywords'
            tabIndex={1}
            value={queryInput}
          />
          <span className='input-group-btn d-none d-md-block'>
            <button
              className={`btn btn-secondary btn-block ${styles.HeaderBoxBorder}`}
              tabIndex={2}
              type='submit'
            >
              <i className='fal icon-search' />
              <span className='ms-15 input-button'>Search</span>
            </button>
          </span>
          <span className='input-group-btn d-block d-md-none w-100 mt-15'>
            <button
              className={`btn btn-secondary btn-block w-100 ${styles.HeaderBoxBorder}`}
              tabIndex={2}
              type='submit'
            >
              <i className='fal icon-search' />
              <span className='ms-15 input-button'>Search</span>
            </button>
          </span>
        </div>
      </div>
    </form>
  );

  const renderPostedDateDropdown: JSX.Element = (
    <TypeaheadSelectBootstrap5
      id='posted-date'
      inputProps={{
        'aria-label': 'posted date filter',
        tabIndex: 3,
        className: 'form-control',
      }}
      placeholder='Posted date'
      multiple={false}
      onChange={(options: Array<string>) => {
        setPageNumber(1);
        setPostedDate(options.length ? options[0] : undefined);
      }}
      options={POSTED_DATE_OPTIONS}
      selected={postedDate ? [postedDate] : []}
    />
  );

  const renderAuthorFilter: JSX.Element = (
    <AutocompleteBootstrap5
      id='author-autocomplete'
      inputProps={{
        'aria-label': 'author filter',
        tabIndex: 4,
        className: 'form-control',
      }}
      placeholder='Author'
      selected={authors}
      minLength={2}
      multiple={true}
      onSelect={(options) => {
        setPageNumber(1);
        setAuthors(options.map((entry) => entry?.text || entry));
      }}
      filterBy={(option) => (authors || []).every((author) => author !== option.text)}
      apiPath={autocompleteAuthorsPath}
    />
  );

  const renderTopicDropdown: JSX.Element = (
    <TypeaheadSelectBootstrap5
      id='topic-selection'
      inputProps={{
        'aria-label': 'topic filter',
        tabIndex: 5,
        className: 'form-control',
      }}
      placeholder='Topic'
      multiple={true}
      options={topicOptions || []}
      selected={topics || []}
      onChange={(options: Array<string>) => {
        setPageNumber(1);
        setTopics(options);
      }}
    />
  );

  const isClearFilterDisabled: boolean =
    !query &&
    !postedDate &&
    !authors?.length &&
    !topics?.length &&
    !isLoading &&
    !savedSearchId &&
    !sponsored;

  const renderSecondaryFiltersForUA: JSX.Element = (
    <div className='row align-items-baseline'>
      <div className='col-12 col-md-4 col-lg-3 col-xl-2 mb-md-0 mb-15 pe-md-15'>
        {renderPostedDateDropdown}
      </div>
      <div className='col-12 col-md-4 col-lg-3 col-xl-4 mb-md-0 mb-15 px-md-1'>
        {renderAuthorFilter}
      </div>
      <div className='col-12 col-md-4 col-lg-3 col-xl-4 px-md-15'>{renderTopicDropdown}</div>
      <div className='col-12 col-lg-3 col-xl-2 pt-25 pt-lg-0 ps-md-15'>
        <span className='input-group-btn w-100 mt-lg-0 mt-25'>
          <button
            data-testid='clear-filters-button'
            disabled={isClearFilterDisabled}
            onClick={clearAllFilters}
            className='btn btn-outline-secondary w-100'
            tabIndex={6}
          >
            <i className='fal icon-ban' />
            <span className='ms-15 input-button'>Clear filters</span>
          </button>
        </span>
      </div>
    </div>
  );

  const renderSecondaryFiltersForAA: JSX.Element = (
    <div className='row align-items-baseline'>
      <div className='col-12 col-md-4 col-lg-2 mb-md-0 mb-15 pe-md-15'>
        {renderPostedDateDropdown}
      </div>
      <div className='col-12 col-md-4 col-lg-3 mb-md-0 mb-15 px-md-1'>{renderAuthorFilter}</div>
      <div className='col-12 col-md-4 col-lg-3 px-md-15'>{renderTopicDropdown}</div>
      <div className='col-6 col-lg-2 pt-25 pt-lg-0 px-lg-15'>
        <span className='input-group-btn w-100 mt-25 mt-md-0'>
          <button
            className='btn btn-outline-primary w-100'
            tabIndex={6}
            onClick={() => setDisplaySavedSearches(!displaySavedSearches)}
            aria-expanded={displaySavedSearches}
            aria-controls='savedSearchesList'
          >
            <i className='fal icon-bell' />
            <span className='mx-15 input-button'>Alerts</span>
            <i className={`fal icon-angle-${displaySavedSearches ? 'up' : 'down'}`} />
          </button>
        </span>
      </div>
      <div className='col-6 col-lg-2 pt-25 pt-lg-0 ps-lg-15'>
        <span className='input-group-btn w-100 mt-md-0 mt-25'>
          <button
            data-testid='clear-filters-button'
            disabled={isClearFilterDisabled}
            onClick={clearAllFilters}
            className='btn btn-outline-secondary w-100'
            tabIndex={7}
          >
            <i className='fal icon-ban' />
            <span className='ms-15 input-button'>Clear filters</span>
          </button>
        </span>
      </div>
    </div>
  );

  return (
    <div className={`mb-48 ${headerStyles.ReusedSearchHeader} ${styles.Header}`}>
      <div className={`container ${headerStyles.ReusedSearchHeaderContainer}`}>
        <h1 className={`mb-25 ${headerStyles.ReusedSearchHeaderTitle}`}>News Search</h1>
        <div className={`px-35 pt-35 pb-2 pb-md-35 ${styles.HeaderBox}`}>{renderKeywordInput}</div>
        <div className={`ps-xs-3 pt-xs-0 py-25 px-35 mb-0 ${styles.HeaderSecondary}`}>
          {isSignedIn ? renderSecondaryFiltersForAA : renderSecondaryFiltersForUA}
        </div>
        <Collapse in={displaySavedSearches} data-testid='saved-searches-list'>
          <div id='savedSearchesList'>
            <div className={`p-35 ${styles.HeaderSearches}`}>
              <SavedSearches />
            </div>
            <div className={`p-35 ${styles.HeaderSearchCreation}`}>
              <SearchCreation />
            </div>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default Container;
